import { GROUND, LAND, LAND_EARTH, hoursMs, res, type EarthId } from '@somnhold/content';
import { cellId } from '../grid.ts';
import { DIRS, hexKey, hexLen, parseHexKey, type Hex } from '../hex.ts';
import { imin } from '../math/fixed.ts';
import { landReach } from '../land.ts';
import type { LandDef, MapDef } from '../types.ts';
import { addRes, event, sortedCellKeys } from './core.ts';
import { eraOf } from './eras.ts';
import type { PlayerId } from '../types.ts';
import type { CampaignState, Job, Ms } from './types.ts';

/*
 * Earthworks on terraced land (docs/OBRAS.md §5): jobs that change the land itself. Each one is checked so the land
 * never shuts the keep off nor grows a wall nobody can break: no new drop of two levels or new cliff, every rim zone
 * still walks up to the keep (with every earthwork planned, and again when each is done), each cell at most a level
 * from what it was, and a cap on how many cells the bastion changes. The map is replaced, never written in place,
 * so every cache keyed by it lets go by itself.
 */

/** The land as it would be with this earthwork done on cell id `id`, or null when it can't be done there. */
const shaped = (land: LandDef, id: number, e: EarthId): { level: number; kind: number; cliff: number } | null => {
  const k = land.kind[id] as number;
  const lv = land.level[id] as number;
  const open = k === GROUND.open || k === GROUND.ruin;
  switch (e) {
    case 'clear':
      return k === GROUND.wood ? { level: lv, kind: GROUND.open, cliff: land.cliff[id] as number } : null;
    case 'quarry':
      return k === GROUND.rock ? { level: lv, kind: GROUND.open, cliff: land.cliff[id] as number } : null;
    case 'raise':
      // A raised cell is never a cliff: it ramps to whatever it meets.
      return open && lv < LAND.top ? { level: lv + 1, kind: k, cliff: 0 } : null;
    case 'lower':
      return open && lv > 0 ? { level: lv - 1, kind: k, cliff: land.cliff[id] as number } : null;
    case 'ditch':
      return open ? { level: lv, kind: GROUND.ditch, cliff: land.cliff[id] as number } : null;
    case 'ford':
      return k === GROUND.water ? { level: lv, kind: GROUND.ford, cliff: land.cliff[id] as number } : null;
  }
};

/** A copy of the land with these changes written in (the arrays are new; the old ones stay as they were). */
const withChanges = (land: LandDef, changes: { id: number; level: number; kind: number; cliff: number }[]): LandDef => {
  const level = land.level.slice();
  const kind = land.kind.slice();
  const cliff = land.cliff.slice();
  for (const c of changes) {
    level[c.id] = c.level;
    kind[c.id] = c.kind;
    cliff[c.id] = c.cliff;
  }
  return { ...land, level, kind, cliff };
};

/** Whether the changed cell keeps every step round it walkable as before: no new drop of two levels, no new cliff. */
const edgesKept = (R: number, before: LandDef, after: LandDef, h: Hex, natural: LandDef | null): boolean => {
  const id = cellId(R, h.q, h.r);
  for (const d of DIRS) {
    const n = cellId(R, h.q + d.q, h.r + d.r);
    if (n < 0) continue;
    const a0 = before.level[id] as number;
    const b0 = before.level[n] as number;
    const a1 = after.level[id] as number;
    const b1 = after.level[n] as number;
    const diff = a1 > b1 ? a1 - b1 : b1 - a1;
    if (diff >= 2) return false;
    // A one-level step ramps unless its higher cell is a cliff: lowering a cell beside a cliff would make a new one.
    const was = a0 === b0 ? false : (a0 > b0 ? before.cliff[id] : before.cliff[n]) === 1;
    const is = a1 === b1 ? false : (a1 > b1 ? after.cliff[id] : after.cliff[n]) === 1;
    // Going back to its own height may bring back the cliff the land had there (never a new one).
    if (is && !was && !(natural && (natural.level[id] as number) === a1 && (natural.level[n] as number) === b1)) return false;
  }
  return true;
};

/** Whether every rim zone still walks up to the keep (one search from the keep: steps are walkable both ways or neither). */
const keepReached = (map: MapDef, land: LandDef): boolean => {
  const R = map.radius;
  const seen = landReach(R, land, cellId(R, 0, 0));
  return land.zones.every((z) => seen[cellId(R, z.q, z.r)] === 1);
};

/** The earthwork jobs planned or under way, by cell. */
const pending = (s: CampaignState, skip?: string): { key: string; e: EarthId }[] => {
  const out: { key: string; e: EarthId }[] = [];
  for (const key of sortedCellKeys(s)) {
    const j = s.cells[key]?.job;
    if (j?.kind === 'earth' && j.earth && key !== skip) out.push({ key, e: j.earth });
  }
  return out;
};

/** What a cell first was, before any earthwork (the «one level from its own» rule). */
const firstOf = (s: CampaignState, key: string, land: LandDef, id: number): { level: number; kind: number } => {
  const w = s.earthworks?.find((x) => x.key === key);
  return w ? { level: w.level, kind: w.kind } : { level: land.level[id] as number, kind: land.kind[id] as number };
};

/** Applies a list of earthworks to the land in order (each on the land the ones before left), or null if one can't be done. */
const applyAll = (map: MapDef, land: LandDef, list: { key: string; e: EarthId }[]): LandDef | null => {
  let cur = land;
  for (const w of list) {
    const h = parseHexKey(w.key);
    const id = cellId(map.radius, h.q, h.r);
    const c = shaped(cur, id, w.e);
    if (!c) return null;
    cur = withChanges(cur, [{ id, ...c }]);
  }
  return cur;
};

/** Why earthwork `e` can't go on (q, r) now, or null when it can. */
export const earthFail = (s: CampaignState, q: number, r: number, e: EarthId, skip?: string): string | null => {
  const land = s.map.land;
  if (!land) return 'notLand';
  if (eraOf(s) < (LAND_EARTH.era[e] ?? 99)) return 'eraLocked';
  const R = s.map.radius;
  const id = cellId(R, q, r);
  if (id < 0 || hexLen(q, r) > R) return 'notBuildable';
  // Never on the keep's plateau.
  if (hexLen(q, r) <= LAND.crest) return 'crest';
  const key = hexKey({ q, r });
  if (s.cells[key] && key !== skip) return 'occupied';
  const h = { q, r };
  const c = shaped(land, id, e);
  if (!c) return 'wrongGround';
  // At most a level from what it first was.
  const first = firstOf(s, key, land, id);
  if (c.level > first.level + 1 || c.level < first.level - 1) return 'tooFar';
  // Quarrying the rock a foundry digs would leave it with nothing.
  if (e === 'quarry' && DIRS.some((d) => s.cells[hexKey({ q: q + d.q, r: r + d.r })]?.built?.structure === 'foundry' || foundryAt(s, q + d.q, r + d.r))) return 'foundryRock';
  // The caps: cells changed in all (done and planned), and ditches.
  const done = s.earthworks ?? [];
  const plans = pending(s, skip);
  const changed = new Set([...done.map((w) => w.key), ...plans.map((w) => w.key)]);
  if (!changed.has(key) && changed.size >= (LAND_EARTH.cap[imin(eraOf(s), LAND_EARTH.cap.length) - 1] ?? 0)) return 'earthCap';
  if (e === 'ditch') {
    let ditches = plans.filter((w) => w.e === 'ditch').length;
    for (let i = 0; i < land.kind.length; i++) if (land.kind[i] === GROUND.ditch) ditches++;
    if (ditches >= LAND_EARTH.ditchCap) return 'ditchCap';
  }
  // No new wall of land: the steps round it stay walkable.
  const after = withChanges(land, [{ id, ...c }]);
  // The land as it first was round here: a cell going back to its own height may bring its old cliff back.
  const natural = c.level === first.level ? withChanges(after, (s.earthworks ?? []).map((w) => {
    const p = parseHexKey(w.key);
    const wid = cellId(R, p.q, p.r);
    return { id: wid, level: w.level, kind: after.kind[wid] as number, cliff: after.cliff[wid] as number };
  })) : null;
  if (!edgesKept(R, land, after, h, natural)) return 'steep';
  // The keep stays reachable: with this one alone, and with every other earthwork planned done too.
  if (!keepReached(s.map, after)) return 'shutsKeep';
  const all = applyAll(s.map, after, plans);
  if (all && !keepReached(s.map, all)) return 'shutsKeep';
  return null;
};

/** Whether a cell is one of a standing foundry's. */
const foundryAt = (s: CampaignState, q: number, r: number): boolean => {
  const part = s.cells[hexKey({ q, r })]?.part;
  return !!part && s.cells[part]?.built?.structure === 'foundry';
};

/** The job for earthwork `e` (paid like any other, then dug by the golems). */
export const earthJob = (s: CampaignState, e: EarthId, pid: PlayerId, now: Ms): Job => ({
  id: s.nextId++,
  kind: 'earth',
  earth: e,
  structure: 'wall',
  level: 1,
  material: 1,
  cost: res({ ...LAND_EARTH.cost[e] }),
  paid: res({}),
  stage: 'blueprint',
  progressMs: 0,
  totalMs: hoursMs(LAND_EARTH.hours[e]),
  placedBy: pid,
  placedAt: now,
  fundedAt: 0,
});

/**
 * An earthwork is done: checked again (the land may have changed since it was planned) and, if it still holds, the
 * land changes and what it gives goes to the store; if not, what was paid comes back. The cell is left free.
 */
export const completeEarth = (s: CampaignState, key: string, now: Ms): void => {
  const cell = s.cells[key];
  const j = cell?.job;
  const land = s.map.land;
  if (!cell || !j || j.kind !== 'earth' || !j.earth || !land) return;
  const h = parseHexKey(key);
  const why = earthFail(s, h.q, h.r, j.earth, key);
  delete s.cells[key];
  if (why) {
    addRes(s.storage, j.paid);
    event(s, now, 'earth', { pid: j.placedBy, cellKey: key, data: { e: j.earth, failed: why } });
    return;
  }
  const id = cellId(s.map.radius, h.q, h.r);
  const c = shaped(land, id, j.earth) as { level: number; kind: number; cliff: number };
  if (!(s.earthworks ?? []).some((w) => w.key === key)) (s.earthworks ??= []).push({ key, level: land.level[id] as number, kind: land.kind[id] as number });
  s.map = { ...s.map, land: withChanges(land, [{ id, ...c }]), earth: (s.map.earth ?? 0) + 1 };
  addRes(s.storage, LAND_EARTH.give[j.earth]);
  event(s, now, 'earth', { pid: j.placedBy, cellKey: key, data: { e: j.earth } });
};

/** For the board: the cell ids an earthwork plan would change (just the one), and whether it may go there. */
export const earthPreview = (s: CampaignState, q: number, r: number, e: EarthId): { ok: boolean; why: string | null } => {
  const why = earthFail(s, q, r, e);
  return { ok: !why, why };
};

